/**
 * widgets/grid.ts — sdílený rozměrový + responsivní standard widgetů.
 * Používá ho Dashboard i WidgetPanel, ať je geometrie na jednom místě.
 *
 * Phone layout (data-layout=phone): vždy 1 sloupec + mobile viewport.
 */
import { useEffect, useState } from 'react';
import { useIsPhoneLayout } from '../lib/ui-layout';
import type { Viewport, WidgetFormat } from './types';

// Grid: 4 buňky (xl), 2 (md), 1 (mobil). Span se zmenšuje s gridem → na telefonu
// je všechno 1 sloupec (auto-reflow).
export const SPAN: Record<WidgetFormat, string> = {
  sm: 'col-span-1',
  md: 'col-span-1 md:col-span-2',
  lg: 'col-span-1 md:col-span-2 xl:col-span-4',
};

/** Phone: vždy 1 sloupec, žádný md/xl span. */
export const SPAN_PHONE: Record<WidgetFormat, string> = {
  sm: 'col-span-1',
  md: 'col-span-1',
  lg: 'col-span-1',
};

// Standardizovaná min. výška podle formátu — karty v řadě lícují.
export const MIN_H: Record<WidgetFormat, string> = {
  sm: 'min-h-[7.5rem]',
  md: 'min-h-[18rem]',
  lg: 'min-h-[18rem]',
};

export const MIN_H_PHONE: Record<WidgetFormat, string> = {
  sm: 'min-h-0',
  md: 'min-h-0',
  lg: 'min-h-0',
};

export const GRID_CLS = 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4';
export const GRID_CLS_PHONE = 'grid grid-cols-1 gap-3';

export const FORMAT_CYCLE: Record<WidgetFormat, WidgetFormat> = {
  sm: 'md',
  md: 'lg',
  lg: 'sm',
};

/** Další formát v cyklu; respektuje povolené rozměry widgetu (kompatibilita). */
export function nextFormat(current: WidgetFormat, allowed?: WidgetFormat[]): WidgetFormat {
  const ring: WidgetFormat[] = allowed && allowed.length ? allowed : ['sm', 'md', 'lg'];
  const i = ring.indexOf(current);
  return ring[(i + 1) % ring.length] ?? ring[0];
}

/** desktop / mobile — phone layout vynutí mobile. */
export function useViewport(): Viewport {
  const phone = useIsPhoneLayout();
  const [v, setV] = useState<Viewport>(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
      ? 'desktop'
      : 'mobile'
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setV(mq.matches ? 'desktop' : 'mobile');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return phone ? 'mobile' : v;
}
